import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Minus from "@insite/mobius/Icons/Minus";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Plus from "@tIntermatic/Icons/Plus";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

type Props = OwnProps & HasProduct;

export interface INTProductDetailsCertificationsStyles {
    parentWrapper?: InjectableCss;
    image?: LazyImageProps;
    certificationsAccordionSection?: AccordionSectionPresentationProps;
    wrapper?: InjectableCss;
    warningFlag?: InjectableCss;
}

export const attributesStyles: INTProductDetailsCertificationsStyles = {
    parentWrapper: {
        css: css`
            // padding-bottom: 15px;
        `,
    },
    wrapper: {
        css: css`
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        `,
    },
    image: {
        css: css`
            img {
                height: 70px;
                width: 70px;
                // padding: 5px;
                object-fit: contain;
                // margin-right: 2px;
            }
        `,
    },

    certificationsAccordionSection: {
        toggleIconProps: {
            src: Minus,
            size: 15,
        },

        expandIconProps: {
            src: Plus,
            size: 15,
        },

        headerProps: {
            css: css`
                > button {
                    background: #fff;
                    border: none;
                    padding: 13px 9px 13px 15px !important;
                    > span {
                        font-size: 18px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 22px;
                        color: #333333;
                        text-transform: capitalize;
                    }
                }
            `,
        },

        panelProps: {
            css: css`
                border: none;
                padding-top: 0px;
                font-size: 16px;
                font-weight: 300;
                letter-spacing: 0;
                line-height: 24px;
                color: #33333a;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            padding-bottom: 7px;
                        `,
                        css`
                            padding-bottom: 7px;
                        `,
                    ])}
            `,
        },
    },

    warningFlag: {
        css: css`
            font-size: 14px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 17px;
            color: #33333a;
            margin-top: 15px;
            margin-bottom: 11px;
            > a {
                display: inline;
                color: #006bc0;
                font-weight: 600;
            }
        `,
    },
};

const styles = attributesStyles;

const INTProductDetailsCertifications: React.FC<Props> = ({ product: { attributeTypes }, fields }) => {
    const certificationTypes = ["EpiCertifications", "CaliforniaProposition65"];
    if (!attributeTypes || attributeTypes.length === 0) {
        return null;
    }
    const limitedAttributeTypes = (attributeTypes ?? []).filter(attributeType => {
        return certificationTypes.indexOf(attributeType.name) !== -1;
    });

    const californiaAttributes = ["nickel", "lead", "none", "dehp"];
    const generateImageSrc = (value: string) => {
        const californiaAttributePresent = californiaAttributes.includes(value.toLowerCase());
        if (!californiaAttributePresent) {
            return `/UserFiles/img/${value}.jpg`;
        }
    };
    const californiaAttributeTypes = (limitedAttributeTypes ?? []).filter(attributeType => {
        return attributeType.name === "CaliforniaProposition65";
    });
    const warningFlag = californiaAttributeTypes.some(attributeType => {
        let flag;
        attributeType?.attributeValues?.forEach(attribute => {
            if (attribute.value.toLowerCase() === "none") {
                flag = false;
            } else {
                flag = true;
            }
        });
        return flag;
    });

    if (
        !limitedAttributeTypes ||
        limitedAttributeTypes.length === 0 ||
        (limitedAttributeTypes.length === californiaAttributeTypes.length && !warningFlag)
    ) {
        return null;
    }
    return (
        <StyledWrapper {...styles.parentWrapper} data-test-selector="productDetails_attributes">
            {limitedAttributeTypes && (
                <Accordion headingLevel={1} {...styles.wrapper}>
                    <ManagedAccordionSection
                        title={fields.title}
                        data-test-selector="productDetails_certifications"
                        initialExpanded={false}
                        {...styles.certificationsAccordionSection}
                    >
                        {limitedAttributeTypes.map(attributeType => (
                            <>
                                {(attributeType.attributeValues ?? []).slice(0, 10).map(cert => (
                                    <LazyImage
                                        key={cert.value}
                                        src={generateImageSrc(cert.value)}
                                        altText={cert.value}
                                        {...styles.image}
                                    />
                                ))}
                            </>
                        ))}
                        <StyledWrapper>
                            {warningFlag && (
                                <StyledWrapper {...styles.warningFlag}>
                                    {siteMessage("propositionMessage")}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </ManagedAccordionSection>
                </Accordion>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(INTProductDetailsCertifications),
    definition: {
        displayName: "Certifications",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Certifications",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
